/**
 * Formats a value for display by replacing underscores with spaces
 * and converting to title case.
 * 
 * Examples:
 * - "like_new" -> "Like New"
 * - "category_subcategory" -> "Category Subcategory"
 * - "good" -> "Good"
 * 
 * @param value - The value to format
 * @returns The formatted display string, or empty string if value is null/undefined
 */
export function formatDisplayValue(value: string | null | undefined): string {
  if (!value) return '';
  
  return value
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Formats a price value with dollar sign, comma separators for thousands, and 2 decimal places.
 * 
 * Examples:
 * - 1000 -> "$1,000.00"
 * - 10000 -> "$10,000.00"
 * - 1234567.89 -> "$1,234,567.89"
 * - 99.5 -> "$99.50"
 * 
 * @param price - The price value (number or string)
 * @returns The formatted price string with $ prefix and commas
 */
export function formatPrice(price: number | string | null | undefined): string {
  if (price === null || price === undefined) return '$0.00';
  
  const num = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(num)) return '$0.00';
  
  // Format with 2 decimal places
  const fixed = num.toFixed(2);
  
  // Split by decimal point and add commas to integer part
  const parts = fixed.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  return `$${parts.join('.')}`;
}

/**
 * Transforms a Supabase storage image URL to be web-compatible.
 * HEIC/HEIF images from iOS are not supported by web browsers.
 * This function uses Supabase's image transformation to convert them.
 * 
 * @param url - The original image URL
 * @returns The transformed URL for web compatibility
 */
export function getWebImageUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  
  // Check if this is a Supabase storage URL
  const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
  if (!supabaseUrl || !url.includes(supabaseUrl)) {
    return url; // Return as-is if not a Supabase URL
  }
  
  // Only transform if it's an object storage URL (not already a render URL)
  if (!url.includes('/storage/v1/object/public/')) {
    return url;
  }
  
  // Transform to use the render endpoint with format conversion
  // /storage/v1/object/public/{bucket}/{path} -> /storage/v1/render/image/public/{bucket}/{path}
  const transformedUrl = url.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );
  
  // Add query param to request web-compatible format
  // 'origin' format auto-converts HEIC to JPEG while preserving other formats
  return `${transformedUrl}?format=origin`;
}

